import type {
  IAiContentBasedTextResponse,
  IAiGenerateImageResponse,
  IAiLimitationsResponse,
} from "@/shared/types/ai.types";
import { api } from "./api";

const path = (path: string) => `/ai${path}`;

// TESTING PROMPT CHANGES AGAINST A LOCAL BACKEND.
//
// The story-agent prompts live in dotell-backend, so a prompt edit cannot be
// exercised from a dev server pointed at staging — you are still talking to
// the deployed copy. This sends ONLY the story-agent call to a backend on
// :3000 while auth and every other request stay where they were.
//
// Opt in from the browser console, so nothing dangerous is ever committed:
//   localStorage.setItem("sd:story-agent-local", "1")   // then reload
//   localStorage.removeItem("sd:story-agent-local")     // back to normal
//
// An absolute URL bypasses the RTK baseUrl; prepareHeaders still attaches the
// Firebase token, and localhost:4200 is in the backend's CORS whitelist.
// DEV-only: in a built bundle the switch is compiled out, so a stray flag can
// never send the bearer token to localhost.
const STORY_AGENT_RUN_PATH = "/story-agent/run";
export const storyAgentRunUrl = (): string => {
  try {
    if (
      import.meta.env.DEV &&
      localStorage.getItem("sd:story-agent-local") === "1"
    ) {
      return "http://localhost:3000/api/story-agent/run";
    }
  } catch {
    // private mode / no storage — fall through to the normal backend
  }
  return STORY_AGENT_RUN_PATH;
};

/**
 * What `POST /story-agent/run` answers with. `result` is the prompt's
 * Zod-validated response payload — for `agent.dispatch` an AgentResponse.
 *
 * Named (rather than inlined on the mutation) because the streaming endpoint
 * sends the very same object as its final `done` frame; see
 * ./story-agent-stream.ts.
 */
/** POST /ai/video (spec 2026-10-15-ai-video). */
export type AiVideoRequest = {
  prompt: string;
  durationSeconds?: number;
  aspectRatio?: "16:9" | "9:16" | "1:1";
  withAudio?: boolean;
  imageUrl?: string;
  cameraMotion?: string;
};
export type AiVideoStart = { jobId: string; durationSeconds: number; clamped: boolean };
export type AiVideoStatus = {
  status: "queued" | "running" | "done" | "failed";
  url?: string;
  mediaFile?: unknown;
  reason?: string;
};

export type StoryAgentRunResponse = {
  promptId: string;
  result: unknown;
  usage?: unknown;
  hadWebSearch: boolean;
  retried?: boolean;
  echo?: boolean;
};

/**
 * `agent.classify`'s `result` — one verdict on the message the user just
 * sent: does it ask for a change (`edit`), only for an answer (`question`),
 * or could it be read either way (`unsure`)? Mirrors the backend prompt's
 * Zod response schema by hand; see ai/agent/fast-chat.ts for what the panel
 * does with each answer.
 */
export type AgentClassifyResult = {
  kind: "question" | "edit" | "unsure";
};

/**
 * `runStoryAgent({ promptId: "critique" })`'s validated result (Task B3).
 * Mirrors dotell-backend's `critique` prompt responseSchema by hand — see
 * ai/agent/handlers/critique.ts, which renders these as a FindingsCard, and
 * ai/helpers/critique-notes.ts's `parseCritiqueNotes`, kept as the fallback
 * for a backend still returning the old plain-string review.
 */
export type CritiqueNotesResult = {
  notes: Array<{
    slideNumber: number;
    kind: "copy" | "design";
    severity: "info" | "warning";
    text: string;
  }>;
};

/**
 * `analyse-slide-screenshot`'s `result` — the STRUCTURE of a slide the user
 * pasted a picture of, so it can be rebuilt in the deck's own theme.
 *
 * Deliberately not a picture of a layout: `family` names the shape, and every
 * word on the slide is transcribed verbatim into `title` / `sections`, so
 * what comes back is content the add-slide pipeline can bind rather than a
 * description of pixels. Mirrors the backend prompt's Zod response schema by
 * hand — see ai/helpers/screenshot-to-blueprint.ts, which maps it onto the
 * blueprint the pipeline already builds from.
 */
export type SlideScreenshotSpec = {
  family:
    | "cover"
    | "cards"
    | "columns"
    | "timeline"
    | "comparison"
    | "stats"
    | "quote"
    | "list"
    | "image-text"
    | "table"
    | "chart";
  title: string;
  subtitle?: string;
  /** In reading order, at most eight. */
  sections: Array<{
    heading?: string;
    body?: string;
    /** A figure the section features, e.g. "38%". */
    value?: string;
    hasImage: boolean;
    hasIcon: boolean;
  }>;
  notes?: string;
};

export type PickLayoutResponse = {
  layouts: Array<{
    layoutId: string;
    title: string;
    category: string;
    slide: unknown;
  }>;
  // The content shape the backend inferred and filtered layouts on. The
  // frontend's rewrite pass needs the same content kind, or the two halves
  // disagree about what the slide is: the chooser picks a people layout while
  // the copy pass writes department names into its headings.
  shape?: {
    imageKind: "people" | "visual" | "logo" | "none";
    imageCount: number;
    unitCount: number;
    wantsChart: boolean;
    wantsTable: boolean;
    wantsStats: boolean;
  } | null;
};

// PERSISTED AGENT CHATS. Mirrors dotell-backend's /story-agent/chats routes
// by hand — the two repos cannot import from each other, so these types ARE
// the contract. `messages` is deliberately `unknown[]`: the panel owns the
// message shape and validates it on the way back in (see
// ai/helpers/serialize-chat.ts), and the API layer has no business knowing
// what a chat message looks like.
export type AgentChatSummary = {
  id: string;
  title: string;
  // The backend writes an ISO timestamp; older records carry epoch millis.
  // Both reach the panel, so both are in the type — the chat repository
  // coerces whichever arrives.
  updatedAt: number | string;
  messageCount: number;
};

export type AgentChatRecord = {
  id: string;
  storyId: string;
  title: string;
  messages: unknown[];
  updatedAt: number | string;
};

/**
 * `runStoryAgent({ promptId: "chat-title" })`'s validated result. The chat
 * keeps the title derived from the user's opening words when this comes back
 * empty, so `title` is optional here rather than trusted.
 */
export type ChatTitleResult = {
  title?: string;
};

// ACCOUNT-LEVEL SKILLS. Mirrors dotell-backend's /story-agent/skills routes
// by hand — see pages/editor/modules/header/ai/skills/skill-types.ts for the
// frontend's own Skill type (the same fields; skill-repository.ts reads a
// record through `migrateSkill` at the boundary, which also copes with a
// backend from before this shape that still answers `{ steps }` rows).
//
// `GET /skills` lists Storydoc's skills first (ids `builtin:*`, never in the
// DB — the ones every org has, then the ones this org added from the
// marketplace), then the org's own skills, then the caller's personal ones.
// Timestamps are epoch milliseconds.
export type AgentSkillTier = "builtin" | "org" | "personal";

// How the org came to have a Storydoc skill. Only on `builtin:*` rows.
export type AgentSkillSource = "preinstalled" | "installed";

export type AgentSkillRecord = {
  id: string;
  name: string;
  description: string;
  instructions: string;
  checklist: string[];
  params: string[];
  tier: AgentSkillTier;
  autoTrigger: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  source?: AgentSkillSource;
};

// A Storydoc catalogue row as `GET /skills/catalogue` (Storydoc admins only)
// returns it: the skill plus its two switches. `PUT /skills/catalogue/:slug`
// takes either or both flags and answers with the same row.
export type AgentSkillCatalogueRecord = AgentSkillRecord & {
  preinstalled: boolean;
  inMarketplace: boolean;
  // True on a code entry whose words are a Storydoc admin's edit.
  edited?: boolean;
};

export type AgentSkillCatalogueFlags = {
  preinstalled?: boolean;
  inMarketplace?: boolean;
};

// What `POST /skills/catalogue` takes — a Storydoc skill written from the
// app rather than shipped in the backend's code — and what a PUT may carry
// for any row, code entries included: the account stores a code entry's edit
// over its constant, and DELETE on that slug drops the edit again.
// `params` is derived by the account from the {{placeholders}} it finds.
export type AgentSkillCatalogueWriteBody = AgentSkillCatalogueFlags & {
  name: string;
  description?: string;
  instructions: string;
  checklist?: string[];
};

// What `POST /skills` and `PUT /skills/:slug` take. `tier` is baked into the
// id, so a PUT may only repeat the stored tier (a change is a 400; "move" is
// a duplicate plus a delete). Built-ins take no writes at all: PUT and DELETE
// on a `builtin:*` slug are a 403 `{ code: "BUILTIN_READ_ONLY" }`, which is
// why the repository refuses those before sending anything.
export type AgentSkillWriteBody = {
  name: string;
  description?: string;
  instructions: string;
  checklist?: string[];
  tier?: Exclude<AgentSkillTier, "builtin">;
  autoTrigger?: boolean;
};

/**
 * `runStoryAgent({ promptId: "skill-distill" })`'s validated result — Save
 * as skill's "write it for me": the run's own requests and progress lines,
 * turned into a name, a "when to use this" description, and instructions
 * (with a checklist when the run reads as an ordered recipe). Mirrors the
 * backend prompt's Zod response schema by hand; see
 * ai/components/SaveAsSkill.tsx, which opens the dialog pre-filled with it
 * rather than saving anything itself.
 */
export type SkillDistillResult = {
  name: string;
  description: string;
  instructions: string;
  checklist?: string[];
};

export const aiApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getLimitations: builder.query<
      IAiLimitationsResponse,
      {
        type: "image" | "text" | "translation";
      }
    >({
      query: ({ type }) => ({
        url: path("/limitations"),
        method: "GET",
        params: { type },
      }),
    }),
    handleStoryTranslation: builder.mutation({
      query: ({
        system,
        prompt,
        initial,
        signal,
      }: {
        system: string;
        prompt: string;
        initial: boolean;
        signal?: AbortSignal;
      }) => ({
        url: path("/translate"),
        method: "POST",
        body: { system, prompt, initial },
        signal,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (args.initial) {
            dispatch(
              aiApi.util.updateQueryData(
                "getLimitations",
                { type: "translation" },
                (draft) => {
                  const { response, ...rest } = data;
                  return Object.assign(draft, rest);
                },
              ),
            );
          }
        } catch (error) {
          console.log(error);
        }
      },
    }),
    getContentBasedText: builder.mutation<
      IAiContentBasedTextResponse,
      {
        system: string;
        prompt: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ system, prompt, signal }) => ({
        url: path("/content-based-text"),
        method: "POST",
        body: { system, prompt },
        signal,
      }),
    }),
    handleTextAiPrompt: builder.mutation({
      query: ({ prompt, system, signal }) => ({
        url: path("/prompt"),
        method: "POST",
        body: {
          prompt,
          system,
        },
        signal,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData(
              "getLimitations",
              { type: "text" },
              (draft) => {
                const { response, ...rest } = data;
                return Object.assign(draft, rest);
              },
            ),
          );
        } catch (error) {
          console.error(
            "[ai] handleTextAiPrompt limitations update failed:",
            error,
          );
        }
      },
    }),
    getSearchTerm: builder.mutation({
      query: ({
        prompt,
        signal,
        type,
      }: {
        prompt: string;
        type?: string;
        signal?: AbortSignal;
      }) => ({
        url: path("/prompt-search"),
        method: "POST",
        signal,
        body: {
          type,
          prompt,
        },
      }),
    }),
    slideContent: builder.mutation({
      query: ({
        id,
        prompt,
        signal,
        storyBrainId,
      }: {
        id: string;
        prompt: string;
        signal?: AbortSignal;
        storyBrainId: string;
      }) => ({
        url: path("/slide-content"),
        method: "POST",
        body: { id, prompt, storyBrainId },
        signal,
      }),
    }),
    generateImage: builder.mutation<
      IAiGenerateImageResponse,
      {
        prompt: string;
        size?: string;
        /**
         * Source pictures to EDIT rather than generate from nothing.
         *
         * /ai/image already base64s these and hands them to Gemini's image
         * models, which take instruction edits — the capability was built
         * and unreachable because this mutation always sent an empty array.
         * Empty means "generate", which is every existing caller.
         */
        images?: string[];
        /**
         * Change `images[0]` as the prompt says, keeping everything else and
         * its shape — rather than drawing a new picture that uses it as a
         * reference (/ai/image type "edit", 2026-10-15).
         */
        edit?: boolean;
        signal?: AbortSignal;
      }
    >({
      // Route through the Gemini-backed /ai/image endpoint — the same one
      // the media-lib "Generate image" feature uses. The legacy
      // /ai/generateImage route is OpenAI DALL·E-3 and returns 400 in our
      // environments, so the agent's image generation was failing while
      // the regular feature worked. `size` is kept in the signature for
      // call-site compatibility; /ai/image derives dimensions from
      // settings.aspectRatio, so we send the same defaults the working
      // feature sends (empty settings → square image).
      query: ({ prompt, images, edit, signal }) => ({
        url: path("/image"),
        method: "POST",
        signal,
        body: {
          type: edit && images?.length ? "edit" : "image",
          model: "basic",
          prompt,
          images: images ?? [],
          settings: {
            layout: "",
            aspectRatio: "",
            style: "",
            view: "",
            background: "",
            colors: [],
            variant: "",
          },
        },
      }),
    }),
    // AI video (spec 2026-10-15-ai-video): start a job; ask how it is.
    startAiVideo: builder.mutation<AiVideoStart, AiVideoRequest>({
      query: (body) => ({ url: path("/video"), method: "POST", body }),
    }),
    getAiVideoStatus: builder.mutation<AiVideoStatus, { jobId: string }>({
      query: ({ jobId }) => ({ url: path(`/video/${encodeURIComponent(jobId)}`), method: "GET" }),
    }),
    validatePayload: builder.mutation({
      query: ({ payload }: { payload: string }) => ({
        url: path("/validate"),
        method: "POST",
        body: { payload },
      }),
    }),
    /**
     * For each input font name that isn't in Google Fonts, ask the backend
     * AI to pick the closest available Google Font. The backend returns a
     * map from input name → resolved Google Font name (or `null` if no
     * good match exists). Used by the design-system brand import flow.
     */
    matchGoogleFonts: builder.mutation<
      { matches: Record<string, string | null> },
      { fonts: string[] }
    >({
      query: ({ fonts }) => ({
        url: path("/match-google-fonts"),
        method: "POST",
        body: { fonts },
      }),
    }),
    // Phase 3 of the AI Story Agent migration. New backend surface lives at
    // POST /api/story-agent/run; the body is { promptId, variables } and the
    // backend looks up the prompt def, Zod-validates the variables, calls
    // OpenAI, then Zod-validates the response against the prompt's
    // responseSchema (retrying once with feedback on failure). The `result`
    // field is the validated response payload — for `agent.dispatch` it's
    // an AgentAction. Behind the VITE_USE_STORY_AGENT_BACKEND flag for now.
    runStoryAgent: builder.mutation<
      StoryAgentRunResponse,
      {
        promptId: string;
        variables: Record<string, unknown>;
        signal?: AbortSignal;
      }
    >({
      query: ({ promptId, variables, signal }) => ({
        url: storyAgentRunUrl(),
        method: "POST",
        body: { promptId, variables },
        signal,
      }),
    }),
    // Layout chooser for the add-slide pipeline: shortlist -> chooseOne ->
    // instantiate on the backend. `count` is 1 when a saved slide already
    // matched (only one alternative is needed) and 2 otherwise. No preview
    // URL comes back; the caller resolves previews by layoutId.
    pickLayout: builder.mutation<
      PickLayoutResponse,
      {
        keyword: string;
        context?: string;
        itemCount?: number;
        count?: 1 | 2;
      }
    >({
      query: (body) => ({
        url: "/story-agent/pick-layout",
        method: "POST",
        body,
      }),
    }),
    // The agent panel's chat history, per story. Called through
    // ai/chats/chat-repository.ts rather than through hooks: persistence runs
    // on a debounce from a long-lived hook, and the history menu reads a list
    // the panel already holds, so nothing here wants a cache subscription.
    listAgentChats: builder.query<
      { chats: AgentChatSummary[] },
      { storyId: string }
    >({
      query: ({ storyId }) => ({
        url: "/story-agent/chats",
        method: "GET",
        params: { storyId },
      }),
    }),
    getAgentChat: builder.query<AgentChatRecord, { id: string }>({
      query: ({ id }) => ({
        url: `/story-agent/chats/${id}`,
        method: "GET",
      }),
    }),
    upsertAgentChat: builder.mutation<
      AgentChatRecord,
      {
        id: string;
        storyId: string;
        title: string;
        messages: unknown[];
        // The message ids this tab already had from the server: the backend
        // keeps any other stored message (another tab's turn) instead of
        // overwriting it.
        knownMessageIds?: string[];
      }
    >({
      query: ({ id, storyId, title, messages, knownMessageIds }) => ({
        url: `/story-agent/chats/${id}`,
        method: "PUT",
        body: { storyId, title, messages, ...(knownMessageIds ? { knownMessageIds } : {}) },
      }),
    }),
    deleteAgentChat: builder.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({
        url: `/story-agent/chats/${id}`,
        method: "DELETE",
      }),
    }),
    // A thumbs up / down on one reply, with the request it answered and the
    // reply itself (dotell-backend story-agent/feedback.service.js). `vote:
    // null` takes it back. Sent through ai/feedback/record-feedback.ts.
    recordAgentFeedback: builder.mutation<
      { ok: boolean },
      {
        messageId: string;
        vote: "up" | "down" | null;
        reason?: string;
        request?: string;
        reply?: string;
        storyId?: string;
        chatId?: string;
        path?: string;
        action?: string;
        skill?: string;
      }
    >({
      query: ({ messageId, ...body }) => ({
        url: `/story-agent/feedback/${encodeURIComponent(messageId)}`,
        method: "PUT",
        body,
      }),
    }),
    // Storydoc staff: every recorded vote, newest first, as a CSV sheet. Read
    // through ai/feedback/download-feedback.ts (the user menu's "Agent
    // feedback (CSV)").
    getAgentFeedbackCsv: builder.query<
      string,
      { vote?: "up" | "down"; limit?: number }
    >({
      query: (params) => ({
        url: "/story-agent/feedback",
        method: "GET",
        params: { ...params, format: "csv" },
        responseHandler: "text",
      }),
    }),
    // The agent panel's saved skills, account-wide (org-scoped on the
    // backend). Called through ai/skills/skill-repository.ts rather than
    // through hooks — same reasoning as the chats endpoints above: the
    // skills store keeps its own in-memory cache for synchronous reads, so
    // nothing here wants a cache subscription.
    listAgentSkills: builder.query<{ skills: AgentSkillRecord[] }, void>({
      query: () => ({
        url: "/story-agent/skills",
        method: "GET",
      }),
    }),
    createAgentSkill: builder.mutation<AgentSkillRecord, AgentSkillWriteBody>({
      query: (body) => ({
        url: "/story-agent/skills",
        method: "POST",
        body,
      }),
    }),
    updateAgentSkill: builder.mutation<
      AgentSkillRecord,
      { slug: string } & AgentSkillWriteBody
    >({
      query: ({ slug, ...body }) => ({
        url: `/story-agent/skills/${slug}`,
        method: "PUT",
        body,
      }),
    }),
    // A skill the account no longer has is a 404, not an `ok` — the caller
    // treats that as "already gone" (skill-repository.ts's `isNotFound`).
    deleteAgentSkill: builder.mutation<{ ok: boolean }, { slug: string }>({
      query: ({ slug }) => ({
        url: `/story-agent/skills/${slug}`,
        method: "DELETE",
      }),
    }),
    // Storydoc's marketplace: the catalogue skills this org can still add.
    // A backend from before round four has no such route (404), which the
    // settings page names as the reason rather than showing an empty list.
    listMarketplaceSkills: builder.query<{ skills: AgentSkillRecord[] }, void>({
      query: () => ({
        url: "/story-agent/skills/marketplace",
        method: "GET",
      }),
    }),
    // Any member adds; `slug` may be bare or `builtin:`-prefixed. Answers with
    // the skill record carrying `source` ("installed", or "preinstalled" when
    // every org already had it).
    installAgentSkill: builder.mutation<AgentSkillRecord, { slug: string }>({
      query: (body) => ({
        url: "/story-agent/skills/installs",
        method: "POST",
        body,
      }),
    }),
    // Org admins only (403 `NOT_ORG_ADMIN`); 404 when it was never installed.
    uninstallAgentSkill: builder.mutation<{ ok: boolean }, { slug: string }>({
      query: ({ slug }) => ({
        url: `/story-agent/skills/installs/${slug}`,
        method: "DELETE",
      }),
    }),
    // Personal → team, for the skill's creator. The tier is baked into the
    // id, so the account copies the document into the org scope and deletes
    // the personal one; the record returned carries the NEW id.
    promoteAgentSkill: builder.mutation<AgentSkillRecord, { id: string }>({
      query: ({ id }) => ({
        url: `/story-agent/skills/${id}/promote`,
        method: "POST",
      }),
    }),
    // Storydoc admins only (403 `NOT_STORYDOC_ADMIN`).
    getSkillCatalogue: builder.query<
      { skills: AgentSkillCatalogueRecord[] },
      void
    >({
      query: () => ({
        url: "/story-agent/skills/catalogue",
        method: "GET",
      }),
    }),
    // Flags and content on any row — a code entry's content becomes its edit.
    updateSkillCatalogueFlags: builder.mutation<
      AgentSkillCatalogueRecord,
      { slug: string } & Partial<AgentSkillCatalogueWriteBody>
    >({
      query: ({ slug, ...body }) => ({
        url: `/story-agent/skills/catalogue/${slug}`,
        method: "PUT",
        body,
      }),
    }),
    createSkillCatalogueEntry: builder.mutation<
      AgentSkillCatalogueRecord,
      AgentSkillCatalogueWriteBody
    >({
      query: (body) => ({
        url: "/story-agent/skills/catalogue",
        method: "POST",
        body,
      }),
    }),
    // Removes a row the catalogue owns, and every org's install of it. A
    // code entry is a 400 — flip its switches instead.
    // Removes an authored row (`{ ok: true }`), or drops a code entry's edit
    // and answers the entry as the code ships it.
    deleteSkillCatalogueEntry: builder.mutation<
      { ok: boolean } | AgentSkillCatalogueRecord,
      { slug: string }
    >({
      query: ({ slug }) => ({
        url: `/story-agent/skills/catalogue/${slug}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetLimitationsQuery,
  useLazyGetLimitationsQuery,
  useHandleStoryTranslationMutation,
  useGetContentBasedTextMutation,
  useHandleTextAiPromptMutation,
  useGetSearchTermMutation,
  useSlideContentMutation,
  useGenerateImageMutation,
  useStartAiVideoMutation,
  useGetAiVideoStatusMutation,
  useValidatePayloadMutation,
  useMatchGoogleFontsMutation,
  useRunStoryAgentMutation,
  usePickLayoutMutation,
} = aiApi;
